<template>
  <div id="app" class="container-fluid">
    <vue-title :title="`YouMDB - ${displayedProfile.name}`"></vue-title>
    <div class="row">
      <div class="col">
        <div v-show="!profile.token_id">
            <h1>Sign in with Google to get started</h1>
          </div>
        <div class="float float-right">
          <div v-show="!profile.token_id">
            <span id="google-signin-button"></span>
          </div>
          <div>
            <img :src="profile.imageUrl" height="36px">
            {{profile.name}}
          </div>
        </div>

        <div v-show="!displayedProfile.name">
          <b-badge variant="danger">Specified user does not exist</b-badge>
        </div>

        <form class="form-inline mt-2" v-if="profile.token_id">
          <router-link to="/" class="text-decoration-none"><span class="logo">YouMDB</span></router-link>
          <div class="input-group mb-2 mr-sm-2" v-if="isDisplayedUser">
            <input type="text" class="form-control" v-model="movieToAdd" placeholder="search" v-on:keyup.enter="movieToAdd && AddMovieOrDisplayList(movieToAdd)">
            <div class="input-group-append">
              <button
                type="button"
                class="btn btn-primary"
                @click="AddMovieOrDisplayList(movieToAdd)"
                :disabled="!movieToAdd"
              >Add</button>
            </div>
          </div>

          <input
            type="text"
            ref="filter"
            class="form-control mb-2 mr-sm-2"
            v-model="filter"
            placeholder="filter"
          >

          <b-form-select class="mb-2 mr-sm-2" v-model="yearSeenFilter" :options="movieYears('DateSeen', 'Year seen')" @change="yearSeenChanged"></b-form-select>
          <b-form-select class="mb-2 mr-sm-2" v-model="releaseDateFilter" :options="movieYears('ReleaseDate', 'Release year')"></b-form-select>

          <b-form-checkbox class="mb-2 mr-sm-2" v-model="showSeen" v-show="isDisplayedUser">Show watched movies</b-form-checkbox>
          <b-form-checkbox class="mb-2 mr-sm-2" v-model="showOnlyInTheater">Only show movies in theater</b-form-checkbox>
        </form>
      </div>

      <div class="row">
        <b-alert
          v-for="alert in alerts"
          :key="alert"
          v-model="alert.visible"
          v-bind:variant="alert.variant"
          dismissible
          @dismissed="alert.visible = false"
        >{{alert.message}}</b-alert>
      </div>
    </div>
    <div class="row" v-if="profile.token_id">
      <div class="ml-2" v-if="displayedUserMovies">
        <b-badge variant="primary">{{displayedProfile.name}}</b-badge>
        {{moviesSeenThisYear}} movies seen this year, on track for
        {{moviesSeenThisYear * 1.0 / (dayOfYear / 365.0) | round}}
        <a target="_blank" style="margin-left: 10px;" v-if="zip" :href="`http://www.fandango.com/redirect.aspx?searchby=location&location=${this.zip}`">Fandango</a>
      </div>
    </div>

    <div v-show="profile.token_id">
      <b-table
        class="clickable-table"
        hover
        small
        :items="filteredMovies"
        :fields="fields"
        :filter="filter"
        :tbody-tr-class="rowClass"
        @row-clicked="rowClicked"
      >
        <template v-slot:cell(Add)="data">
          <span @click.stop="AddMovieOrDisplayList(data.item.ImdbId)" style="color: green;"><i :class=seenStatus(data.item) class=""></i></span>
        </template>
      </b-table>
    </div>

    <b-modal
      :id="infoModal.id"
      size="lg"
      :title="infoModal.content.Name"
      :ok-disabled="!isDisplayedUser"
      @hide="resetInfoModal"
      @ok="UpdateUserMovieWrapper"
    >
      <div v-if="infoModal.content" class="container">
        <div class="row">
          <div class="col-sm">
            <!-- <v-rating v-model="infoModal.content.Score"></v-rating> -->

            <b-form-group id="input-group-score" label="Score:" label-for="score">
              <b-form-input
                id="score"
                :disabled="!isDisplayedUser"
                v-model="infoModal.content.Score"
                type="number"
                min="0"
                max="100"
                placeholder="0-100"
                :state="scoreState"
              ></b-form-input>
            </b-form-group>

            <b-form-group id="input-group-date" label="Date Seen:" label-for="date">
              <datepicker
                id="date"
                :disabled="!isDisplayedUser"
                v-model="infoModal.content.DateSeen"
                :format="formatDate"
                :highlighted="{ dates: [ new Date() ]}"
                typeable
                bootstrap-styling
                :state="scoreState"
              ></datepicker>
            </b-form-group>

            <b-form-group id="input-group-notes" label="Notes:" label-for="notes">
              <b-form-input
                id="notes"
                :disabled="!isDisplayedUser"
                v-model="infoModal.content.Notes"
                placeholder="Notes"
              ></b-form-input>
            </b-form-group>

            <b-table stacked :fields="infoModalFields" :items="[infoModal.content]">
              <template v-slot:cell(ImdbId)="data">
                <a target="_blank"
                  v-bind:id="data.item.ImdbId"
                  v-bind:href="`https://www.imdb.com/title/${data.item.ImdbId}`"
                >{{data.value}}</a>
              </template>
              <template v-slot:cell(Awards)="data">
                {{data.value && data.value.map(a => {
                  if (a.Winner) {
                    return `${a.Name} (Winner)`;
                  } else {
                    return a.Name;
                  }
                }).join(", ")}}
              </template>
            </b-table>

            <div style="margin-bottom: 5px">
              <a target="_blank" :href="`${this.infoModal.content.TomatoUrl}`">View movie on RottenTomatoes</a><br/>
              <a target="_blank" :href="`https://www.justwatch.com/us/search?q=${this.infoModal.content.Name}`">Search for movie on JustWatch.com</a>
            </div>
          </div>
          <div class="col-sm">
            <img v-if="infoModal.content.PosterUrl" :src="infoModal.content.PosterUrl">
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      :id="addModal.id"
      size="lg"
      :title="addModal.title"
      @hide="addModal.content = null"
      @ok="AddMovieFromModal"
      :ok-disabled="!this.addModal.selectedItem"
    >
      <div v-if="addModal.content" class="container">
        <div class="row">
          <div class="col-sm">
            <b-table
              class="clickable-table"
              hover
              small
              :items="addModal.content"
              @row-clicked="searchRowClicked"
            >
              <template v-slot:cell(ImdbId)="data">
                <a target="_blank" v-bind:href="`https://www.imdb.com/title/${data.value}`">{{data.value}}</a>
              </template>
              <template v-slot:cell(PosterUrl)="data">
                <img v-if="data.value" :src="data.value" height="100px">
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import Datepicker from "vuejs-datepicker";
import { log } from "util";
import { pathToFileURL } from 'url';
import VueTitle from './components/VueTitle.vue';
import { createWriteStream } from 'fs';
const moment = require("moment");

const url = process.env.VUE_APP_API_BASE;

export default {
  name: "app",
  components: { Datepicker, VueTitle },
  directives: {},
  watch: {
    '$route' (to, from) {
      if (to.query != from.query) {
        console.log(to.query);
      }
      if (to.params.id !== from.params.id) {
        this.OnUserChange();
      }
    },
    'filter' (value) {
      this.$router.replace({ ...this.$router.currentRoute, query: { ...this.$route.query, filter: value || undefined } });
    },
    'showOnlyInTheater' (value) {
      this.$router.replace({ ...this.$router.currentRoute, query: { ...this.$route.query, showOnlyInTheater: value || undefined } });
    },
    'showSeen' (value) {
      this.$router.replace({ ...this.$router.currentRoute, query: { ...this.$route.query, showSeen: value || undefined } });
      ["Score","DateSeen"].forEach(key => {
        const field = _.find(this.fields, f => f.key === key);
        field.thClass = field.tdClass = value ? '' : 'd-none';
      });
    },
    'releaseDateFilter' (value) {
      this.$router.replace({ ...this.$router.currentRoute, query: { ...this.$route.query, releaseDateFilter: value || undefined } });
    },
    'yearSeenFilter' (value) {
      this.$router.replace({ ...this.$router.currentRoute, query: { ...this.$route.query, yearSeenFilter: value || undefined } });
    },
    'displayedMovie' (value) {
      this.$router.replace({ ...this.$router.currentRoute, query: { ...this.$route.query, displayedMovie: value || undefined } });
    }
  },
  data: function() {
    return {
      alerts: [],
      fields: [],
      infoModalFields: [],
      userMovies: {},
      displayedUserMovies: [],
      searchResults: [],
      filter: null,
      movieToAdd: undefined,
      dayOfYear: moment().dayOfYear(),
      displayedProfile: {},
      profile: {},
      displayedUserId: undefined,
      isDisplayedUser: false,
      zip: null,
      showSeen: true,
      showOnlyInTheater: false,
      yearSeenFilter: null,
      releaseDateFilter: null,
      displayedMovie: null,
      infoModal: {
        id: "info-modal",
        title: "",
        content: {}
      },
      addModal: {
        id: "add-modal",
        title: "Search results",
        content: {},
        selectedItem: null
      },
      defaultFields: [
        { key: "Rank", sortable: true },
        { key: "ScoreAgedRating", label: "Aged Rating", sortable: true, sortDirection: "desc" },
        {
          key: "DateSeen",
          formatter: "formatDate",
          sortable: true,
          sortDirection: "desc"
        },
        { key: "Name", sortable: true },
        {
          key: "ReleaseDate",
          formatter: "formatDate",
          sortable: true,
          sortDirection: "desc"
        },
        { key: "Score", sortable: true, sortDirection: "desc" }
      ]
    };
  },
  methods: {
    movieYears(field, text) {
      const movieYears = _.uniq(
        this.displayedUserMovies
          .filter(m => m[field])
          .map(m => new Date(m[field]).getFullYear())
      )
        .sort()
        .reverse();

      movieYears.unshift({ value: null, text: text });
      return movieYears;
    },
    OnGoogleAuthSuccess(user) {      
      console.log("TCL: ENTERING OnGoogleAuthSuccess -> OnGoogleAuthSuccess", user.getAuthResponse());

      this.profile.token_id = user.getAuthResponse().id_token;
      const profile = user.getBasicProfile();
      this.profile.name = profile.getGivenName();
      this.profile.imageUrl = profile.getImageUrl();

      localStorage.setItem("profile", JSON.stringify(this.profile));
      console.log("OnGoogleAuthSuccess -> this.profile", this.profile);

      this.AddTokenRefreshHandler();
      this.Initialize();
    },
    OnGoogleAuthFail(error) {
      this.alerts.push({
        visible: true,
        variant: "danger",
        message: error
      });
    },
    Initialize() {
      console.log("Initialize");

      this.GetUserId();
      this.GetZipCode();

      if (this.displayedMovie && this.displayedUserMovies[this.displayedMovie]) {
        this.showInfoModalWithMovie(this.displayedUserMovies[this.displayedMovie]);
      }
    },
    AddTokenRefreshHandler() {
      // Function that will be called to refresh authorization
      console.log("Adding automatic token refresh handler");
      const refreshAuthLogic = failedRequest => {
        console.log("TCL: Got 401 error, refreshing token and trying again");

        // hack: https://github.com/google/google-api-javascript-client/issues/232#issuecomment-278348739
        return Promise.resolve(gapi.auth2.getAuthInstance().currentUser.get().reloadAuthResponse()).then(r =>
        {
          this.profile.token_id = r.id_token;
          localStorage.setItem("profile", JSON.stringify(this.profile));
          failedRequest.response.config.headers['token_id'] = this.profile.token_id;
          return Promise.resolve();
        });
      };

      createAuthRefreshInterceptor(axios, refreshAuthLogic);
    },
    GetZipCode() {
      // navigator.geolocation.getCurrentPosition(position => console.log(position));
      return axios.get(`https://geolocation-db.com/json/`).then(response => {
        this.zip = response.data.postal;
      });
    },
    AddMovieOrDisplayList(query) {
      if (/^tt\d+/.test(query)) {
        if (!this.userMovies[this.profile.userId].some(m => m.ImdbId === query)) {
          this.AddMovie(query);
        }
      } else {
        this.SearchAndDisplayModal(query);
      }
    },
    AddMovieFromModal(bvModalEvt) {
      this.AddMovie(this.addModal.selectedItem.ImdbId);
    },
    SearchAndDisplayModal(query) {
      return axios.get(`${url}/movies/search/${query}`).then(response => {
        this.searchResults = response.data;
        this.addModal.content = this.searchResults.map(m =>
          this.searchResultProperties(m)
        );
        this.$root.$emit("bv::show::modal", this.addModal.id);
      });
    },
    AddMovie(imdbId) {
      axios.put(`${url}/usermovies/${imdbId}`, null, {
        headers: { token_id: this.profile.token_id }
      })
      .then(response => {
        console.log("TCL: AddMovie (success): response", response)
        
        if (!response) return;

        this.$bvToast.toast(`Successfully added ${response.data.Name}`, {
          title: `Success`,
          variant: "success"
        });

        const imdbId = response.data.ImdbId;
        this.GetUserMovies(this.profile.userId).then(movies => {
          
          // Need to reset variable so seenStatus refreshes
          const temp = this.userMovies;
          this.userMovies = undefined;
          this.userMovies = temp;

          this.userMovies[this.profile.userId] = movies;
          const newMovie = _.find(movies, m => m.ImdbId === imdbId);
          if (newMovie) {
            newMovie.JustAdded = true;
          }

          console.log("movies updated", this.userMovies[this.profile.userId]);
          
          if (this.isDisplayedUser) {
            this.displayedUserMovies = movies;
            setTimeout(() => {
              const element = document.querySelectorAll("tr.table-success")[0];
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 0);
          }
        });
      })
      .catch(error => {
        console.log("TCL: AddMovie: error", error);
        if (error.response) {
          if (error.response.status === 303) {
            this.$bvToast.toast(
              `${error.response.data.Name} is already in your list`,
              {
                title: `No change`,
                variant: "warning"
              }
            );
          } else {
            this.alerts.push({
              visible: true,
              variant: "danger",
              message: error
            });
          }
        } else if (error.request) {
          console.log("TCL: AddMovie: error.request", error.request);
        }
        else {
          console.log("TCL: AddMovie: error.message", error.message);
        }
      });
    },
    UpdateUserMovieWrapper() {
      this.UpdateUserMovie(this.infoModal.content);
    },
    UpdateUserMovie(clone) {
      console.log("TCL: UpdateUserMovie -> UpdateUserMovie", clone)
      const name = clone.Name;
      const original = clone.original;

      axios
        .post(`${url}/usermovies/update`, clone, {
          headers: { token_id: this.profile.token_id }
        })
        .then(response => {
          original.Score = clone.Score;
          original.DateSeen = clone.DateSeen;

          this.$bvToast.toast(`Updated ${name}`, {
            title: `Success`,
            variant: "success"
          });
        })
        .catch(error => {
            this.alerts.push({
              visible: true,
              variant: "danger",
              message: error
            });
        });
    },
    OnUserChange(userId) {
      const parsed = parseInt(this.$route.params.id);
      if (!isNaN(parsed)) {
        this.displayedUserId = parsed;
      } else {
        this.displayedUserId = this.profile.userId;
      }
      
      this.isDisplayedUser = !this.displayedUserId || this.displayedUserId === this.profile.userId;

      if (!this.isDisplayedUser) {
        this.fields.unshift({ key: "Add", label: "Add" });
        this.GetUser(this.displayedUserId).then(() => {
          
          if (this.userMovies[this.displayedUserId]) {
            console.log("Getting movies from cache for", this.displayedUserId);
            this.displayedUserMovies = this.userMovies[this.displayedUserId];
          } else {            
            this.GetUserMovies(this.displayedUserId).then(movies => {
              this.userMovies[this.displayedUserId] = this.displayedUserMovies = movies;
            });
          }
        });
      } else {
        this.displayedProfile.name = this.profile.name;
        if (this.fields[0] && this.fields[0].key === "Add") {
          this.fields = [...this.defaultFields];
        }
        this.displayedUserMovies = this.userMovies[this.profile.userId];
      }
    },
    GetUserId() {
      return axios.get(`${url}/users`, { headers: { token_id: this.profile.token_id } }).then(response => { 
        this.profile.userId = response.data;

        this.GetUserMovies(this.profile.userId).then(movies => {
          this.userMovies[this.profile.userId] = movies;

          this.OnUserChange(this.profile.userId);

          setTimeout(() => {
            this.$refs.filter.focus();
          }, 0);
        });
      });
    },
    GetUser(userId) {
      return axios.get(`${url}/users/${userId}`, { headers: { token_id: this.profile.token_id } }).then(response => { 
        this.displayedProfile.name = response.data.GivenName;
      });
    },
    GetUserMovies(userId) {
      console.log("TCL: GetUserMovies -> userId", userId);
      
      let userMoviesUrl = `${url}/usermovies/${userId}`;

      return axios
        .get(userMoviesUrl, {
          headers: { token_id: this.profile.token_id }
        })
        .then(response => response.data);
    },
    rowClass(item, type) {
      if (!item) return;
      if (item.JustAdded) {
        return "table-success";
      }
      if (item.DateSeen || item.Score) {
        return "table-secondary";
      }
      if (this.isInTheater(item)) {
        return "table-primary";
      }
    },
    isInTheater(item) {
        return moment(item.ReleaseDate).isBetween(moment().add(-2, "month"), moment().add(5, "day"));
    },
    rowClicked(item, index, event) {
      this.showInfoModalWithMovie(item);
    },
    searchRowClicked(item, index, event) {
      this.addModal.selectedItem = item;
    },
    yearSeenChanged(value) {
      if (value) {
        this.showSeen = true;
      }
    },
    resetInfoModal() {
      this.infoModal.title = "";
      this.infoModal.content = {};
    },
    formatDate(value) {
      if (!value) return null;
      return moment(String(value)).format("MM/DD/YYYY");
    },
    movieProperties(movie) {
      const showProperties = [
        "ImdbId",
        "Runtime",
        "ReleaseDate",
        "ScoreRTCritic",
        "ScoreRTAudience",
        "ScoreMetacritic",
        "ScoreImdb",
        "ScoreWeightedAverage"
      ];

      // return [_.pick(movie, showProperties)];
    },
    searchResultProperties(movie) {
      const showProperties = ["ImdbId", "Name", "Year", "PosterUrl"];

      return _.pick(movie, showProperties);
    },
    setPropertyFromQuery(property) {
      const value = this.$route.query[property];
      if (value) {
        this[property] = value;
      }
    },
    setIntPropertyFromQuery(property, objectToSet = this) {
      const value = this.$route.query[property];
      const parsed = parseInt(value);
      if (!isNaN(parsed)) {
        objectToSet[property] = parsed;
      }
    },
    setBoolPropertyFromQuery(property) {
      const value = this.$route.query[property];
      if (value === "true") {
        this[property] = true;
      } 
      else {
        this[property] = false;
      }
    },
    seenStatus(movie) {
      console.log(movie.name);
      const userMovie = this.userMovies[this.profile.userId].find(m => m.MovieId === movie.MovieId);
      return !userMovie ? "fas fa-user-plus" : (userMovie.DateSeen || userMovie.Score) ? "fas fa-check-square" : "";
    },
    showInfoModalWithMovie(movie) {
      const clone = { ...movie };
      clone.original = movie;
      this.infoModal.content = clone;
      this.$root.$emit("bv::show::modal", this.infoModal.id);
    }
  },
  computed: {
    moviesSeenThisYear() {
      return this.displayedUserMovies.filter(
        m =>
          m.DateSeen && new Date(m.DateSeen).getFullYear() === moment().year()
      ).length;
    },
    filteredMovies() {
      const moviesToShow = this.displayedUserMovies.filter(
        m =>
          (this.showSeen || !this.isDisplayedUser || (!m.DateSeen && !m.Score)) &&
          (!this.showOnlyInTheater || this.isInTheater(m)) &&
          (!this.yearSeenFilter || (m.DateSeen && new Date(m.DateSeen).getFullYear() === this.yearSeenFilter)) &&
          (!this.releaseDateFilter || (m.ReleaseDate && new Date(m.ReleaseDate).getFullYear() === this.releaseDateFilter))
      );

      let index = 1;
      moviesToShow.forEach(m => (m.Rank = index++));

      return moviesToShow;
    },
    scoreState() {
      return (
        this.infoModal.content.Score &&
        this.infoModal.content.Score >= 0 &&
        this.infoModal.content.Score <= 100
      );
    }
  },
  created() {
    window.addEventListener('keydown', (e) => {
      if (e.key == '/') {
        this.$refs.filter.focus();
        e.preventDefault();
      }
    });
  },
  mounted() {
    console.log("mounted");
    const profileJson = localStorage.getItem("profile");
    this.profile = JSON.parse(profileJson) || {};
    
    console.log("mounted -> this.profile", this.profile);    

    if (!this.profile.token_id) {
      console.log("mounted -> gapi.signin2.render");
      gapi.signin2.render("google-signin-button", {
        onsuccess: this.OnGoogleAuthSuccess
      });
    } else {
      console.log("mounted -> Initialize")
      gapi.signin2.render("google-signin-button");
      this.AddTokenRefreshHandler();
      this.Initialize();
    }

    console.log("TCL: mounted -> this.$route.query", this.$route.query)
    this.setPropertyFromQuery("filter");
    this.setPropertyFromQuery("displayedMovie");
    this.setIntPropertyFromQuery("yearSeenFilter");
    this.setIntPropertyFromQuery("releaseDateFilter");
    this.setBoolPropertyFromQuery("showOnlyInTheater");
    this.setBoolPropertyFromQuery("showSeen");

    this.fields = [...this.defaultFields];

    this.infoModalFields = [
      { key: "ImdbId", label: "IMDB ID" },
      { key: "Runtime" },
      { key: "ReleaseDate", formatter: d => this.formatDate(d) },
      { key: "ScoreRTCritic", label: "RottenTomatoes Critic" },
      { key: "ScoreRTAudience", label: "RottenTomatoes Audience" },
      { key: "ScoreMetacritic", label: "Metacritic" },
      { key: "ScoreImdb", label: "IMDB" },
      { key: "ScoreWeightedAverage", label: "Weighted Average" },
      { key: "Awards" }
    ];
  }
};
</script>

<style lang="scss">
#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

.logo {
  display: inline-block;
  font-size: 1.3em;
  background-color: #f5c619;
  color: black;
  border-radius: 5px;
  padding: 4px 6px 4px 6px;
  margin-top: -8px;
  margin-right: 8px;
  font-family: Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif;
}

.clickable-table td {
  cursor: pointer;
}
</style>
